import type { EventQuestion, EventQuestionType, LocalizedText, QuestionAnswer } from '../types';

/**
 * Client mirror of functions/src/modules/events/eventQuestions.ts.
 *
 * The server is the authority — it re-validates everything submitted here and
 * does not trust the client. This file exists so the booking form can give a
 * devotee useful, immediate feedback instead of a round-trip 400, and so admin
 * screens can render questions/answers without duplicating the rules per
 * component. Keep the constants and behaviour here in lockstep with the
 * server file; a drift just means the client accepts something the server
 * then rejects (or vice versa), not a security hole.
 */

const QUESTION_TYPES: readonly EventQuestionType[] = [
    'text', 'textarea', 'select', 'radio', 'checkbox',
];

/** Types whose answer must be one (or more) of the declared option values. */
const OPTION_TYPES: readonly EventQuestionType[] = ['select', 'radio', 'checkbox'];

/** The only type that yields multiple values. */
const MULTI_TYPES: readonly EventQuestionType[] = ['checkbox'];

// Bounds mirror the server's — see eventQuestions.ts on the backend for why.
export const MAX_QUESTIONS_PER_EVENT = 40;
export const MAX_OPTIONS_PER_QUESTION = 30;
const MAX_TEXT_ANSWER = 500;
const MAX_TEXTAREA_ANSWER = 2000;
const MAX_LABEL = 300;

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const localized = (value: unknown): LocalizedText => {
    const source = (value ?? {}) as Record<string, unknown>;
    return {
        en: text(source.en).slice(0, MAX_LABEL),
        si: text(source.si).slice(0, MAX_LABEL),
    };
};

/** A label with only one side filled falls back to the other, as elsewhere in the app. */
export const questionLabel = (label: LocalizedText, language: string): string =>
    (language === 'si' ? label.si || label.en : label.en || label.si) || '';

/**
 * Normalises whatever the event API returned into questions we trust enough
 * to render. Anything malformed is DROPPED rather than thrown: a bad question
 * must not take down the booking form for the rest of the event.
 *
 * Mirrors normalizeQuestions on the server exactly (same drop rules), so a
 * question that would be silently rejected there never gets this far anyway —
 * this is a defensive second pass against stale caches or hand-edited data,
 * not the primary line of defense.
 */
export function normalizeQuestions(raw: unknown): EventQuestion[] {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const questions: EventQuestion[] = [];

    for (const entry of raw.slice(0, MAX_QUESTIONS_PER_EVENT)) {
        const source = (entry ?? {}) as Record<string, unknown>;
        const id = text(source.id).slice(0, 64);
        const type = text(source.type) as EventQuestionType;
        if (!id || seen.has(id) || !QUESTION_TYPES.includes(type)) continue;

        const label = localized(source.label);
        if (!label.en && !label.si) continue;

        const question: EventQuestion = { id, label, type };
        if (source.required === true) question.required = true;

        const help = localized(source.helpText);
        if (help.en || help.si) question.helpText = help;

        if (OPTION_TYPES.includes(type)) {
            const options: EventQuestion['options'] = [];
            const rawOptions = Array.isArray(source.options) ? source.options : [];
            const values = new Set<string>();
            for (const rawOption of rawOptions.slice(0, MAX_OPTIONS_PER_QUESTION)) {
                const optionSource = (rawOption ?? {}) as Record<string, unknown>;
                const value = text(optionSource.value).slice(0, 200);
                if (!value || values.has(value)) continue;
                const optionLabel = localized(optionSource.label);
                values.add(value);
                options.push({
                    value,
                    label: (optionLabel.en || optionLabel.si) ? optionLabel : { en: value, si: value },
                });
            }
            // A choice question with no choices cannot be answered, so it is not
            // a question — dropping it beats rendering an empty, dead control.
            if (options.length === 0) continue;
            question.options = options;
        }

        seen.add(id);
        questions.push(question);
    }

    return questions;
}

/** In-progress form state: one entry per question id, keyed the same way the controls write to it. */
export type AnswerValue = string | string[];
export type AnswerMap = Record<string, AnswerValue>;

export interface AnswerValidationError {
    questionId: string;
}

/**
 * Returns the ids of every question that fails validation:
 *  - a required question with no answer, or
 *  - a select/radio/checkbox answer holding a value outside the question's
 *    own options (this can only happen if the form state was tampered with
 *    or the question's options changed under the devotee — the UI's own
 *    controls never produce it — but it is treated as unanswered rather than
 *    silently accepted, matching how buildAnswersPayload drops it).
 *
 * Returns every failing question, not just the first, so the form can point
 * at each one at once instead of a single round-trip per fix.
 */
export function validateAnswers(questions: EventQuestion[], answers: AnswerMap): AnswerValidationError[] {
    const errors: AnswerValidationError[] = [];

    for (const question of questions) {
        if (!isAnswered(question, answers)) {
            if (question.required) errors.push({ questionId: question.id });
        }
    }

    return errors;
}

/** True when the question has a real, in-bounds answer (see validateAnswers). */
function isAnswered(question: EventQuestion, answers: AnswerMap): boolean {
    const raw = answers[question.id];
    const allowed = OPTION_TYPES.includes(question.type)
        ? new Set((question.options ?? []).map((option) => option.value))
        : undefined;

    if (MULTI_TYPES.includes(question.type)) {
        const list = Array.isArray(raw) ? raw : [];
        const valid = list.filter((value) => !allowed || allowed.has(value));
        return valid.length > 0;
    }

    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) return false;
    if (allowed && !allowed.has(value)) return false;
    return true;
}

/**
 * The wire-format shape `buildAnswersPayload` returns and both enrollment
 * submission paths (BookingModal's direct POST, WaitlistModal via
 * EventsContext.addToWaitlist) send to the server — distinct from
 * `Booking.answers` (`QuestionAnswer[]`), which is the server's stored,
 * label/type-snapshotted form.
 */
export type AnswerPayload = Array<{ questionId: string; value: AnswerValue }>;

/**
 * Builds the wire-format payload the enrollment endpoint expects:
 * `[{ questionId, value }]`, `value` a string for text/textarea/select/radio
 * and a string[] for checkbox. Unanswered questions (and out-of-bounds
 * selections — see validateAnswers) are omitted rather than sent as empty,
 * matching how the server stores answers.
 */
export function buildAnswersPayload(
    questions: EventQuestion[],
    answers: AnswerMap,
): AnswerPayload {
    const payload: AnswerPayload = [];

    for (const question of questions) {
        const raw = answers[question.id];
        const allowed = OPTION_TYPES.includes(question.type)
            ? new Set((question.options ?? []).map((option) => option.value))
            : undefined;

        if (MULTI_TYPES.includes(question.type)) {
            const list = Array.isArray(raw) ? raw : [];
            const value = [...new Set(list.filter((v) => !allowed || allowed.has(v)))];
            if (value.length > 0) payload.push({ questionId: question.id, value });
            continue;
        }

        const value = typeof raw === 'string' ? raw.trim().slice(
            0,
            question.type === 'textarea' ? MAX_TEXTAREA_ANSWER : MAX_TEXT_ANSWER,
        ) : '';
        if (!value) continue;
        if (allowed && !allowed.has(value)) continue;
        payload.push({ questionId: question.id, value });
    }

    return payload;
}

/**
 * Renders stored answers as label/value pairs for display (e.g. an admin
 * booking detail view). Falls back to the option VALUE when a label can't be
 * resolved — the question may have been edited or deleted since — so a
 * report never shows a blank where the devotee did in fact answer.
 */
export function formatAnswers(
    answers: QuestionAnswer[],
    language: string,
    questions: EventQuestion[] = [],
): Array<{ label: string; value: string }> {
    const optionLabel = (questionId: string, value: string): string => {
        const option = questions
            .find((question) => question.id === questionId)?.options
            ?.find((candidate) => candidate.value === value);
        return option ? questionLabel(option.label, language) || value : value;
    };

    return answers.map((answer) => ({
        label: questionLabel(answer.label, language) || answer.questionId,
        value: Array.isArray(answer.value)
            ? answer.value.map((entry) => optionLabel(answer.questionId, entry)).join(', ')
            : optionLabel(answer.questionId, answer.value),
    }));
}
